import { useEffect, useMemo, useState } from "react"
import { Alert, Button, ButtonGroup, Col, Container, Dropdown, FloatingLabel, Form, Modal, OverlayTrigger, Row, Spinner, Table, Tooltip } from "react-bootstrap"
import Notification from "../components/Notification"
import useTableData from "../hooks/useTableData"
import { useAutoRefresh } from "../hooks/useAutoRefresh"
import {
  createPoptavka,
  deletePoptavka,
  fetchPoptavky,
  fetchPoptavkySuggestions,
  updatePoptavka,
} from "../services/api/poptavky"

const toNum = (v) => {
  if (v === null || v === undefined || v === "") return 0
  const n = Number(String(v).replace(",", ".").trim())
  return Number.isFinite(n) ? n : 0
}

const toTrim = (v) => String(v ?? "").trim()

const toDateInput = (value) => {
  if (!value) return ""
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ""
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

const toDateDisplay = (value) => {
  const iso = toDateInput(value)
  if (!iso) return ""
  const [y, m, d] = iso.split("-")
  return `${d}.${m}.${y}`
}

const defaultForm = {
  id_poptavky: "",
  datum: "",
  popis: "",
  zakaznik: "",
  dodavatel: "",
  pocet: "",
  cena: "",
  poznamka: "",
  stav: "0",
}

const FILTER_STORAGE_KEY = "requests_active_filters_v1"

const loadStoredFilters = () => {
  const defaults = {
    filtr: {},
    dateInput: "",
    dodavatelInput: "",
    popisInput: "",
    zakaznikInput: "",
    pocetInput: "",
    cenaInput: "",
  }
  try {
    if (typeof window === "undefined") return defaults
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY)
    if (!raw) return defaults
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return defaults
    if (parsed.filtr && typeof parsed.filtr === "object") {
      return {
        filtr: parsed.filtr,
        dateInput: String(parsed.dateInput ?? ""),
        dodavatelInput: String(parsed.dodavatelInput ?? ""),
        popisInput: String(parsed.popisInput ?? ""),
        zakaznikInput: String(parsed.zakaznikInput ?? ""),
        pocetInput: String(parsed.pocetInput ?? ""),
        cenaInput: String(parsed.cenaInput ?? ""),
      }
    }
    // backward compatibility with previously stored plain "filtr" object
    return { ...defaults, filtr: parsed }
  } catch {
    return defaults
  }
}

const hasAnyFilter = (obj) => !!obj && Object.keys(obj).length > 0


const Requests = () => {
  const storedFilterState = loadStoredFilters()
  const REQUEST_STATUSES = [
    { code: 0, label: "nevyřízeno", icon: "bi-clipboard-check", color: 'text-muted', bgColor: 'rgba(var(--bs-secondary-rgb), 0.03)', sort: 0 },
     { code: 2, label: "naskladněno", icon: "bi-box2", color: 'text-warning', bgColor: 'rgba(var(--bs-warning-rgb), 0.16)', sort: 1 },
    { code: 1, label: "vyřízeno", icon: "bi-check-circle", color: 'text-success', bgColor: 'rgba(var(--bs-success-rgb), 0.12)', sort: 2 },   
  ]

  const getRequestStatus = (code) =>
    REQUEST_STATUSES.find((s) => s.code === Number(code)) || REQUEST_STATUSES[0]

  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [notifications, setNotifications] = useState([])

  const [isFiltrOpen, setIsFiltrOpen] = useState(false)
  const [filtr, setFiltr] = useState(storedFilterState.filtr)
  const [dateInput, setDateInput] = useState(storedFilterState.dateInput || toTrim(storedFilterState.filtr.datum))
  const [dodavatelInput, setDodavatelInput] = useState(storedFilterState.dodavatelInput || toTrim(storedFilterState.filtr.dodavatel))
  const [popisInput, setPopisInput] = useState(storedFilterState.popisInput || toTrim(storedFilterState.filtr.popis))
  const [zakaznikInput, setZakaznikInput] = useState(storedFilterState.zakaznikInput || toTrim(storedFilterState.filtr.zakaznik))
  const [pocetInput, setPocetInput] = useState(storedFilterState.pocetInput || "")
  const [cenaInput, setCenaInput] = useState(storedFilterState.cenaInput || "")
  const [isDateInvalid, setIsDateInvalid] = useState(false)
  const [isPocetInvalid, setIsPocetInvalid] = useState(false)
  const [isCenaInvalid, setIsCenaInvalid] = useState(false)

  const [showFormModal, setShowFormModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isDeleteError, setIsDeleteError] = useState(false)
  const [validated, setValidated] = useState(false)
  const [formMode, setFormMode] = useState("create")
  const [formData, setFormData] = useState(defaultForm)
  const [suggestions, setSuggestions] = useState({ dodavatel: [], zakaznik: [], popis: [] })
  const [selectedItem, setSelectedItem] = useState(null)

  useEffect(() => {
    setFiltr((prev) => {
      if (!prev?.stredisko) return prev
      const { stredisko, ...rest } = prev
      return rest
    })
  }, [])

  useEffect(() => {
    try {
      if (typeof window === "undefined") return
      const hasInputs = [dateInput, dodavatelInput, popisInput, zakaznikInput, pocetInput, cenaInput]
        .some((v) => String(v || "").trim() !== "")
      if (hasAnyFilter(filtr) || hasInputs) {
        window.localStorage.setItem(
          FILTER_STORAGE_KEY,
          JSON.stringify({
            filtr,
            dateInput,
            dodavatelInput,
            popisInput,
            zakaznikInput,
            pocetInput,
            cenaInput,
          })
        )
      } else {
        window.localStorage.removeItem(FILTER_STORAGE_KEY)
      }
    } catch {
      // ignore localStorage errors
    }
  }, [filtr, dateInput, dodavatelInput, popisInput, zakaznikInput, pocetInput, cenaInput])

  const loadData = async () => {
    try {
      setIsLoading(true)
      const rows = await fetchPoptavky({
        stav: filtr.stav ?? "",
      })
      const mapped = (rows || []).map((r) => {
        const status = getRequestStatus(r.stav)
        return {
          id_poptavky: r.id_poptavky,
          id_uzivatele: r.id_uzivatele,
          datum: r.datum,
          vytvoreno: r.datum,
          popis: toTrim(r.popis),
          zakaznik: toTrim(r.zakaznik),
          dodavatel: toTrim(r.dodavatel),
          pocet: Number(r.pocet || 0),
          pocet_text: Number(r.pocet || 0).toLocaleString("cs-CZ", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          cena: Number(r.cena || 0),
          poznamka: toTrim(r.poznamka),
          stav: Number(r.stav || 0),
          stav_label: status?.label || "",
          stav_icon: status?.icon || "",
          stav_color: status?.color || "",
          stav_bg: status?.bgColor || "",
          stav_sort: Number(status?.sort ?? r.stav ?? 0),
          uzivatel_jmeno: toTrim(r.uzivatel_jmeno),
        }
      })
      setItems(mapped)
    } catch (error) {
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), type: "error", message: error?.message || "Nepodařilo se načíst poptávky." },
      ])
    } finally {
      setIsLoading(false)
      setHasLoaded(true)
    }
  }

  useEffect(() => {
    loadData()
  }, [filtr.stav])

  useAutoRefresh(loadData, {
    shouldRun: () => !showFormModal && !showDeleteModal && !isSaving && !isDeleting && !isLoading,
    deps: [showFormModal, showDeleteModal, isSaving, isDeleting, isLoading, filtr.stav],
  })

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isFiltrOpen &&
        !event.target.closest(".offcanvas-custom") &&
        !event.target.closest("#offcanvas-custom-btn") &&
        !event.target.closest(".filter-active-item") &&
        !event.target.closest(".btn-clear-filters")
      ) {
        setIsFiltrOpen(false)
      }
    }
    const handleEscape = (event) => {
      if (event.key === "Escape" && isFiltrOpen) {
        setIsFiltrOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleEscape)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [isFiltrOpen])

  useEffect(() => {
    if (!showFormModal) {
      setSuggestions({ dodavatel: [], zakaznik: [], popis: [] })
      return
    }

    const fields = ["dodavatel", "zakaznik", "popis"]
    const timers = []

    fields.forEach((field) => {
      const value = toTrim(formData[field])
      if (value.length < 3) {
        setSuggestions((prev) => ({ ...prev, [field]: [] }))
        return
      }

      const timer = setTimeout(async () => {
        try {
          const list = await fetchPoptavkySuggestions({
            field,
            q: value,
            limit: 10,
          })
          setSuggestions((prev) => ({
            ...prev,
            [field]: Array.isArray(list) ? list : [],
          }))
        } catch {
          setSuggestions((prev) => ({ ...prev, [field]: [] }))
        }
      }, 250)

      timers.push(timer)
    })

    return () => timers.forEach((t) => clearTimeout(t))
  }, [
    showFormModal,
    formData.dodavatel,
    formData.zakaznik,
    formData.popis,
  ])

  const parseDateFilter = (input, setInvalid) => {
    const filter = { datum1: "", datum2: "", datumOperator: "" }
    const dateRegex = /^\d{1,2}\.\d{1,2}\.\d{4}$/
    const v = toTrim(input)
    if (v.includes("..")) {
      const [d1, d2] = v.split("..").map((x) => x.trim())
      if (dateRegex.test(d1) && dateRegex.test(d2)) {
        filter.datum1 = d1
        filter.datum2 = d2
        filter.datumOperator = ".."
      }
    } else if (/^(<|>|<=|>=)/.test(v)) {
      const m = v.match(/^(<|>|<=|>=)(\d{1,2}\.\d{1,2}\.\d{4})$/)
      if (m && dateRegex.test(m[2])) {
        filter.datumOperator = m[1]
        filter.datum1 = m[2]
      }
    } else if (dateRegex.test(v)) {
      filter.datumOperator = "="
      filter.datum1 = v
    }
    setInvalid(v !== "" && !filter.datumOperator)
    return filter
  }

  const parseTextFilter = (input, key) => {
    const value = toTrim(input)
    const opKey = `${key}Operator`
    if (!value) return { [key]: "", [opKey]: "" }
    if (value.endsWith("*")) return { [key]: value.slice(0, -1), [opKey]: "startsWith" }
    return { [key]: value, [opKey]: "=" }
  }

  const parseNumericFilter = (input, key, setInvalid) => {
    const v = toTrim(input).replace(",", ".")
    const key1 = `${key}1`
    const key2 = `${key}2`
    const opKey = `${key}Operator`
    const filter = { [key1]: "", [key2]: "", [opKey]: "" }
    if (v.includes("..")) {
      const [n1, n2] = v.split("..").map((x) => Number(x))
      if (Number.isFinite(n1) && Number.isFinite(n2)) {
        filter[key1] = n1
        filter[key2] = n2
        filter[opKey] = ".."
      }
    } else if (/^(<=|>=|<|>)/.test(v)) {
      const m = v.match(/^(<=|>=|<|>)(\d+(\.\d+)?)$/)
      if (m) {
        filter[key1] = Number(m[2])
        filter[opKey] = m[1]
      }
    } else if (/^\d+(\.\d+)?$/.test(v)) {
      filter[key1] = Number(v)
      filter[opKey] = "="
    }
    setInvalid(v !== "" && !filter[opKey])
    return filter
  }

  const filteredData = useMemo(() => {
    const parseDate = (str) => {
      const [d, m, y] = String(str || "").split(".").map(Number)
      return new Date(y, m - 1, d)
    }
    const dateToKey = (dt) => (
      dt.getFullYear() * 10000 + (dt.getMonth() + 1) * 100 + dt.getDate()
    )
    const evalText = (value, f, op) => {
      if (!f) return true
      const source = String(value || "").toUpperCase()
      const target = String(f || "").toUpperCase()
      if (op === "startsWith") return source.startsWith(target)
      if (op === "=") return source === target
      return source.includes(target)
    }
    const evalNum = (value, op, n1, n2) => {
      if (!op) return true
      const v = Number(value || 0)
      const a = Number(n1)
      const b = Number(n2)
      switch (op) {
        case "..": return v >= a && v <= b
        case "<": return v < a
        case "<=": return v <= a
        case ">": return v > a
        case ">=": return v >= a
        case "=": return v === a
        default: return true
      }
    }
    return items.filter((x) => {
      let dateOk = true
      if (filtr.datumOperator) {
        const itemKey = dateToKey(new Date(x.datum))
        const d1 = filtr.datum1 ? dateToKey(parseDate(filtr.datum1)) : null
        const d2 = filtr.datum2 ? dateToKey(parseDate(filtr.datum2)) : null
        switch (filtr.datumOperator) {
          case "..": dateOk = itemKey >= d1 && itemKey <= d2; break
          case "<": dateOk = itemKey < d1; break
          case "<=": dateOk = itemKey <= d1; break
          case ">": dateOk = itemKey > d1; break
          case ">=": dateOk = itemKey >= d1; break
          case "=": dateOk = itemKey === d1; break
          default: dateOk = true
        }
      } else if (filtr.datum) {
        dateOk = toDateDisplay(x.datum) === filtr.datum
      }

      const dodavatelOk = evalText(x.dodavatel, filtr.dodavatel, filtr.dodavatelOperator)
      const popisOk = evalText(x.popis, filtr.popis, filtr.popisOperator)
      const zakaznikOk = evalText(x.zakaznik, filtr.zakaznik, filtr.zakaznikOperator)
      const pocetOk = evalNum(x.pocet, filtr.pocetOperator, filtr.pocet1, filtr.pocet2)
      const cenaOk = evalNum(x.cena, filtr.cenaOperator, filtr.cena1, filtr.cena2)
      const uzivatelOk = !filtr.uzivatel || String(x.uzivatel_jmeno || "") === String(filtr.uzivatel)
      return dateOk && dodavatelOk && popisOk && zakaznikOk && pocetOk && cenaOk && uzivatelOk
    })
  }, [items, filtr])

  const uzivateleOptions = useMemo(() => {
    return [...new Set(items.map((x) => toTrim(x.uzivatel_jmeno)).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, "cs"))
  }, [items])

  const table = useTableData(filteredData, {
    defaultSortField: "datum",
    defaultSortDirection: "desc",
    searchableFields: ["vytvoreno", "cena", "pocet_text", "datum", "popis", "zakaznik", "dodavatel", "poznamka", "uzivatel_jmeno", "stav_label"],
  })

  const clearAllFilters = () => {
    setFiltr({})
    setDateInput("")
    setDodavatelInput("")
    setPopisInput("")
    setZakaznikInput("")
    setPocetInput("")
    setCenaInput("")
    setIsDateInvalid(false)
    setIsPocetInvalid(false)
    setIsCenaInvalid(false)
  }

  const applyPreset = (presetFn) => {
    setDateInput("")
    setDodavatelInput("")
    setPopisInput("")
    setZakaznikInput("")
    setFiltr(presetFn())
  }

  const presetAllOpen = () => ({
    stav: "0",
  })

  const presetAllStocked = () => ({
    stav: "2",
  })

  const presetAllDone = () => ({
    stav: "1",
  })

  const applyDatePreset = (dayCount) => {
    const end = new Date()
    const start = new Date(end)
    start.setDate(start.getDate() - Math.max(dayCount - 1, 0))
    const datum1 = toDateDisplay(start)
    const datum2 = toDateDisplay(end)
    const dateLabel = dayCount === 1 ? datum2 : `${datum1}..${datum2}`

    setDateInput(dateLabel)
    setDodavatelInput("")
    setPopisInput("")
    setZakaznikInput("")
    setPocetInput("")
    setCenaInput("")
    setIsDateInvalid(false)
    setIsPocetInvalid(false)
    setIsCenaInvalid(false)
    setFiltr(dayCount === 1
      ? { datum1: datum2, datum2: "", datumOperator: "=" }
      : { datum1, datum2, datumOperator: ".." })
  }

  const openCreate = () => {
    setIsError(false)
    setValidated(false)
    setFormMode("create")
    setFormData({
      ...defaultForm,
      datum: toDateInput(new Date()),
      stav: "0",
    })
    setShowFormModal(true)
  }

  const openEdit = (row) => {
    setIsError(false)
    setValidated(false)
    setFormMode("edit")
    setFormData({
      id_poptavky: String(row.id_poptavky),
      datum: toDateInput(row.datum),
      popis: row.popis || "",
      zakaznik: row.zakaznik || "",
      dodavatel: row.dodavatel || "",
      pocet: String(row.pocet ?? ""),
      cena: String(row.cena ?? ""),
      poznamka: row.poznamka || "",
      stav: String(row.stav ?? 0),
    })
    setShowFormModal(true)
  }

  const openDelete = (row) => {
    setSelectedItem(row)
    setIsDeleteError(false)
    setShowDeleteModal(true)
  }

  const handleFormChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    const form = e.currentTarget
    if (!form.checkValidity()) {
      e.stopPropagation()
      setValidated(true)
      return
    }
    setValidated(true)
    setIsSaving(true)
    setIsError(false)
    try {
      const payload = {
        datum: formData.datum,
        popis: toTrim(formData.popis),
        zakaznik: toTrim(formData.zakaznik),
        dodavatel: toTrim(formData.dodavatel),
        pocet: toNum(formData.pocet),
        cena: toNum(formData.cena),
        poznamka: toTrim(formData.poznamka),
        stav: [0, 1, 2].includes(Number(formData.stav)) ? Number(formData.stav) : 0,
      }
      if (formMode === "create") {
        await createPoptavka(payload)
      } else {
        await updatePoptavka(formData.id_poptavky, payload)
      }
      setShowFormModal(false)
      await loadData()
      setNotifications((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: "success",
          message: formMode === "create" ? "Poptávka byla uložena." : "Poptávka byla upravena.",
        },
      ])
    } catch (error) {
      setIsError(true)
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), type: "error", message: error?.message || "Uložení selhalo." },
      ])
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (e) => {
    e.preventDefault()
    if (!selectedItem?.id_poptavky) return
    setIsDeleting(true)
    setIsDeleteError(false)
    try {
      await deletePoptavka(selectedItem.id_poptavky)
      setShowDeleteModal(false)
      setSelectedItem(null)
      await loadData()
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), type: "success", message: "Poptávka byla smazána." },
      ])
    } catch (error) {
      setIsDeleteError(true)
      setNotifications((prev) => [
        ...prev,
        { id: Date.now(), type: "error", message: error?.message || "Smazání selhalo." },
      ])
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      {/* Aktivní filtry */}
      {((filtr.datum1 || filtr.datum2 || filtr.datum) || filtr.dodavatel || filtr.popis || filtr.zakaznik || filtr.pocet1 || filtr.cena1 || filtr.uzivatel || filtr.stav !== undefined) && (
        <h6>Aktuální filtr</h6>
      )}
      <div className="filter-active-wrapper">
        {(filtr.datum1 || filtr.datum2 || filtr.datum) && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Datum</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { datum, datum1, datum2, datumOperator, ...r } = p; return r }) ; setDateInput(""); setIsDateInvalid(false)}}>
              {dateInput} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr.dodavatel && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Dodavatel</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { dodavatel, dodavatelOperator, ...r } = p; return r }) ; setDodavatelInput("")}}>
              {filtr.dodavatel} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr.popis && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Popis</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { popis, popisOperator, ...r } = p; return r }) ; setPopisInput("")}}>
              {filtr.popis} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr.zakaznik && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Zákazník</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { zakaznik, zakaznikOperator, ...r } = p; return r }) ; setZakaznikInput("")}}>
              {filtr.zakaznik} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr.uzivatel && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Vytvořil</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { uzivatel, ...r } = p; return r }) }}>
              {filtr.uzivatel} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {(filtr.pocet1 || filtr.pocet2) && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Počet</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { pocet1, pocet2, pocetOperator, ...r } = p; return r }) ; setPocetInput(""); setIsPocetInvalid(false) }}>
              {pocetInput} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {(filtr.cena1 || filtr.cena2) && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Cena</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => { setFiltr((p) => { const { cena1, cena2, cenaOperator, ...r } = p; return r }) ; setCenaInput(""); setIsCenaInvalid(false) }}>
              {cenaInput} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {filtr.stav !== undefined && (
          <div className="filter-active-item">
            <span className="text-muted ms-1"><small>Stav</small></span>
            <button className="btn btn-outline-primary btn-sm" onClick={() => setFiltr((p) => { const { stav, ...r } = p; return r })}>
              {getRequestStatus(filtr.stav)?.label || ""} <i className="bi bi-trash ms-1 py-1"></i>
            </button>
          </div>
        )}
        {((filtr.datum1 || filtr.datum2 || filtr.datum) || filtr.dodavatel || filtr.popis || filtr.zakaznik || filtr.pocet1 || filtr.cena1 || filtr.uzivatel || filtr.stav !== undefined) && (
          <button className="btn-clear-filters" onClick={clearAllFilters}>
            <i className="bi bi-trash"></i>
          </button>
        )}
      </div>
      {/* Header */}
      <div className="row d-flex align-items-center justify-content-space my-2">
        <div className="col">
          <button className="btn btn-primary me-1" onClick={openCreate}>
            <i className="bi bi-plus-circle me-2"></i>Přidat
          </button>
          <button id="offcanvas-custom-btn" className="btn btn-outline-primary me-1" onClick={() => setIsFiltrOpen((s) => !s)}>
            <i className="bi bi-sliders me-2"></i>Filtr
          </button>
          <Dropdown className="d-inline">
            <Dropdown.Toggle variant="outline-primary" id="requests-quick-filters" title="Rychlé filtry">
              <i className="bi bi-clock-history me-2"></i>Rychlé filtry
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Header>Stav</Dropdown.Header>
              <Dropdown.Item onClick={() => applyPreset(presetAllOpen)}>
                <i className="bi bi-clipboard-check me-2"></i>Vše nevyřízené
              </Dropdown.Item>
              <Dropdown.Item onClick={() => applyPreset(presetAllStocked)}>
                <i className="bi bi-box2 me-2"></i>Vše naskladněné
              </Dropdown.Item>
              <Dropdown.Item onClick={() => applyPreset(presetAllDone)}>
                <i className="bi bi-check-circle me-2"></i>Vše vyřízené
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Header>Období</Dropdown.Header>
              <Dropdown.Item onClick={() => applyDatePreset(1)}>
                <i className="bi bi-calendar-day me-2"></i>Dnes
              </Dropdown.Item>
              <Dropdown.Item onClick={() => applyDatePreset(7)}>
                <i className="bi bi-calendar-week me-2"></i>Poslední týden
              </Dropdown.Item>
              <Dropdown.Item onClick={() => applyDatePreset(30)}>
                <i className="bi bi-calendar3 me-2"></i>Poslední měsíc
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="col">
          <div className="search-wrapper">
            <span className="search-icon"><i className="bi bi-search"></i></span>
            <input
              type="text"
              placeholder="Hledat..."
              value={table.searchQuery}
              onChange={(e) => table.setSearchQuery(e.target.value)}
            />
            {table.searchQuery && (
              <span className="clear-icon" role="button" onClick={() => table.setSearchQuery("")}>
                <i className="bi bi-x"></i>
              </span>
            )}
          </div>
        </div>
      </div>
      {/* Filtr offcanvas */}
      <div className={`offcanvas-custom ${isFiltrOpen ? "open" : ""}`}>
        <div className="d-flex justify-content-between align-items-center p-4 mb-3">
          <h4 className="my-1">Filtrování poptávek</h4>
          <div>
            <button type="button" className="btn" title="Zrušit vše" onClick={clearAllFilters}><i className="bi bi-trash"></i></button>
            <button type="button" className="btn" title="Zavřít" onClick={() => setIsFiltrOpen(false)}><i className="bi bi-x-lg"></i></button>
          </div>
        </div>
        <div className="offcanvas-custom-body">
          <Container className="px-4">
            <Row className="g-1">
              <Col xs={12}>
                <Form.Group controlId="offcanvasDatum">
                  <FloatingLabel label="datum pořízení">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-requests-datum">
                          potvrzení Enter<br/>
                          operátory &lt;, &gt;, &lt;=, &gt;=<br/>
                          rozsah přes ..
                        </Tooltip>
                      }
                    >
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={`${(filtr.datum1 || filtr.datum2 || filtr.datumOperator || filtr.datum) ? "border-primary border-opacity-25" : ""} ${isDateInvalid ? "is-invalid" : ""}`.trim()}
                      value={dateInput}
                      onChange={(e) => setDateInput(e.target.value)}
                      onBlur={(e) => {
                        const parsed = parseDateFilter(e.target.value, setIsDateInvalid)
                        setFiltr((s) => ({ ...s, ...parsed }))
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const parsed = parseDateFilter(e.currentTarget.value, setIsDateInvalid)
                          setFiltr((s) => ({ ...s, ...parsed }))
                        }
                      }}
                    />
                    <Form.Control.Feedback type="invalid">neplatný formát datumu</Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="offcanvasDodavatel">
                  <FloatingLabel label="dodavatel">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="tooltip-requests-dodavatel">potvrzení Enter<br/>lze použít *</Tooltip>}
                    >
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={(filtr.dodavatel || filtr.dodavatelOperator) ? "border-primary border-opacity-25" : ""}
                      value={dodavatelInput}
                      onChange={(e) => setDodavatelInput(e.target.value)}
                      onBlur={(e) => {
                        const parsed = parseTextFilter(e.target.value, "dodavatel")
                        setFiltr((s) => ({ ...s, ...parsed }))
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const parsed = parseTextFilter(e.currentTarget.value, "dodavatel")
                          setFiltr((s) => ({ ...s, ...parsed }))
                        }
                      }}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="offcanvasPopis">
                  <FloatingLabel label="popis">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="tooltip-requests-popis">potvrzení Enter<br/>lze použít *</Tooltip>}
                    >
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={(filtr.popis || filtr.popisOperator) ? "border-primary border-opacity-25" : ""}
                      value={popisInput}
                      onChange={(e) => setPopisInput(e.target.value)}
                      onBlur={(e) => {
                        const parsed = parseTextFilter(e.target.value, "popis")
                        setFiltr((s) => ({ ...s, ...parsed }))
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const parsed = parseTextFilter(e.currentTarget.value, "popis")
                          setFiltr((s) => ({ ...s, ...parsed }))
                        }
                      }}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="offcanvasZakaznik">
                  <FloatingLabel label="zákazník">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="tooltip-requests-zakaznik">potvrzení Enter<br/>lze použít *</Tooltip>}
                    >
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={(filtr.zakaznik || filtr.zakaznikOperator) ? "border-primary border-opacity-25" : ""}
                      value={zakaznikInput}
                      onChange={(e) => setZakaznikInput(e.target.value)}
                      onBlur={(e) => {
                        const parsed = parseTextFilter(e.target.value, "zakaznik")
                        setFiltr((s) => ({ ...s, ...parsed }))
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const parsed = parseTextFilter(e.currentTarget.value, "zakaznik")
                          setFiltr((s) => ({ ...s, ...parsed }))
                        }
                      }}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="offcanvasPocet">
                  <FloatingLabel label="počet">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-requests-pocet">
                          potvrzení Enter<br/>
                          operátory &lt;, &gt;, &lt;=, &gt;=<br/>
                          rozsah přes ..
                        </Tooltip>
                      }
                    >
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={`${(filtr.pocet1 || filtr.pocet2 || filtr.pocetOperator) ? "border-primary border-opacity-25" : ""} ${isPocetInvalid ? "is-invalid" : ""}`.trim()}
                      value={pocetInput}
                      onChange={(e) => setPocetInput(e.target.value)}
                      onBlur={(e) => {
                        const parsed = parseNumericFilter(e.target.value, "pocet", setIsPocetInvalid)
                        setFiltr((s) => ({ ...s, ...parsed }))
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const parsed = parseNumericFilter(e.currentTarget.value, "pocet", setIsPocetInvalid)
                          setFiltr((s) => ({ ...s, ...parsed }))
                        }
                      }}
                    />
                    <Form.Control.Feedback type="invalid">neplatný formát čísla</Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="offcanvasCena">
                  <FloatingLabel label="cena">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-requests-cena">
                          potvrzení Enter<br/>
                          operátory &lt;, &gt;, &lt;=, &gt;=<br/>
                          rozsah přes ..
                        </Tooltip>
                      }
                    >
                      <span className="offcanvas-custom-tooltip ms-2 text-muted" role="button"><i className="bi bi-info-circle"></i></span>
                    </OverlayTrigger>
                    <Form.Control
                      type="text"
                      className={`${(filtr.cena1 || filtr.cena2 || filtr.cenaOperator) ? "border-primary border-opacity-25" : ""} ${isCenaInvalid ? "is-invalid" : ""}`.trim()}
                      value={cenaInput}
                      onChange={(e) => setCenaInput(e.target.value)}
                      onBlur={(e) => {
                        const parsed = parseNumericFilter(e.target.value, "cena", setIsCenaInvalid)
                        setFiltr((s) => ({ ...s, ...parsed }))
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const parsed = parseNumericFilter(e.currentTarget.value, "cena", setIsCenaInvalid)
                          setFiltr((s) => ({ ...s, ...parsed }))
                        }
                      }}
                    />
                    <Form.Control.Feedback type="invalid">neplatný formát částky</Form.Control.Feedback>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="offcanvasUzivatel">
                  <FloatingLabel label="vytvořil">
                    <Form.Select
                      className={filtr.uzivatel ? "border-primary border-opacity-25" : ""}
                      value={filtr.uzivatel || ""}
                      onChange={(e) => {
                        const val = e.target.value
                        setFiltr((s) => {
                          if (!val) {
                            const { uzivatel, ...rest } = s
                            return rest
                          }
                          return { ...s, uzivatel: val }
                        })
                      }}
                    >
                      <option value="">všichni uživatelé</option>
                      {uzivateleOptions.map((u) => (
                        <option key={u} value={u}>{u}</option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group controlId="offcanvasStav">
                  <FloatingLabel label="stav">
                    <Form.Select
                      className={filtr.stav !== undefined ? "border-primary border-opacity-25" : ""}
                      value={filtr.stav ?? ""}
                      onChange={(e) => {
                        const val = e.target.value
                        setFiltr((s) => {
                          if (val === "") {
                            const { stav, ...rest } = s
                            return rest
                          }
                          return { ...s, stav: val }
                        })
                      }}
                    >
                      <option value="">vše</option>
                      <option value="0">nevyřízeno</option>
                      <option value="2">naskladněno</option>
                      <option value="1">vyřízeno</option>                      
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
      {/* Tabulka */}
      <div className="row my-3">
        <div className="col">
          {isLoading && (
            <div className="d-flex align-items-center text-muted small mb-2">
              <Spinner animation="border" size="sm" className="me-2" />
              Načítám poptávky...
            </div>
          )}
          <Table responsive hover className="table-transparent">
            <thead>
            <tr>
              <th onClick={() => table.handleSort("datum")}>
                datum <i className={`bi ${table.sortField === "datum" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i>
              </th>
              <th onClick={() => table.handleSort("dodavatel")}>
                dodavatel <i className={`bi ${table.sortField === "dodavatel" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i>
              </th>
              <th onClick={() => table.handleSort("popis")}>
                popis <i className={`bi ${table.sortField === "popis" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i>
              </th>
              <th onClick={() => table.handleSort("zakaznik")}>
                zákazník <i className={`bi ${table.sortField === "zakaznik" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i>
              </th>
              <th onClick={() => table.handleSort("pocet")}>
                počet <i className={`bi ${table.sortField === "pocet" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i>
              </th>
              <th onClick={() => table.handleSort("cena")}>
                cena <i className={`bi ${table.sortField === "cena" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i>
              </th>
              <th onClick={() => table.handleSort("uzivatel_jmeno")}>
                uživatel <i className={`bi ${table.sortField === "uzivatel_jmeno" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i>
              </th>
              <th style={{ width: "6%", textAlign: "center" }} onClick={() => table.handleSort("stav_sort")}>
                stav <i className={`bi ${table.sortField === "stav_sort" ? (table.sortDirection === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "invisible-icon"}`}></i>
              </th>
              <th style={{ width: "5%" }}>akce</th>
            </tr>
            </thead>
            <tbody>
              {table.data.length > 0 ? (
                table.data.map((row) => {
                  return (
                    <tr
                      key={row.id_poptavky}
                      onDoubleClick={() => openEdit(row)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: row.stav_bg || "",
                      }}
                    >
                      <td>{toDateDisplay(row.datum)}</td>
                      <td>{row.dodavatel}</td>
                      <td>
                        <span className="requests-popis-tablet-truncate" title={String(row.popis || '')}>
                          {String(row.popis || '')}
                        </span>
                      </td>
                      <td>{row.zakaznik}</td>
                      <td>{row.pocet.toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td>{row.cena.toLocaleString("cs-CZ", { style: "currency", currency: "CZK" })}</td>
                      <td>{row.uzivatel_jmeno || ""}</td>
                      <td style={{ width: "6%", textAlign: "center" }}>
                        <span title={row.stav_label}>
                          <i className={`bi ${row.stav_icon} ${row.stav_color}`}></i>
                        </span>
                      </td>
                      <td style={{ width: "5%" }}>
                        <button className="btn-table" onClick={() => openEdit(row)} title="Upravit">
                          <i className="bi bi-pencil-square"></i>
                        </button>
                        <button className="btn-table" onClick={() => openDelete(row)} title="Smazat">
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  )
                })
              ) : hasLoaded ? (
                <tr className="no-data-row">
                  <td colSpan="9" className="border-0">
                    <div className="d-block my-3 text-center">
                      <i className="bi bi-journal-text fs-4"></i>
                      <p className="small">Nebyly nalezeny žádné údaje.</p>
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </div>
      </div>
      {/* Legenda a Stránkování */}
      <div className="row d-flex align-items-center justify-content-between my-2">
        <div className="col-auto d-flex flex-wrap align-items-center gap-1 small">
          {REQUEST_STATUSES.map((status) => (
            <span 
              key={status.code} 
              className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded"
              style={{ backgroundColor: status.bgColor || 'transparent' }}
            >
              <i className={`bi ${status.icon} ${status.color}`}></i>
              <span>{status.label}</span>
            </span>
          ))}
        </div>
        <div className="col pagination small text-end">
          {table.totalItems > 0 && (
            <>
              <span>{table.startIndex + 1} – {Math.min(table.endIndex, table.totalItems)} z {table.totalItems}</span>
              <button className="btn btn-sm border-0 p-0" onClick={table.handlePrevPage} disabled={table.currentPage === 1}><i className="bi bi-chevron-left"></i></button>
              <button className="btn btn-sm border-0 p-0" onClick={table.handleNextPage} disabled={table.currentPage === table.totalPages}><i className="bi bi-chevron-right"></i></button>
              <select value={table.itemsPerPage} onChange={(e) => table.setItemsPerPage(Number(e.target.value))}>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </>
          )}
        </div>
      </div>

      <Modal show={showFormModal} fullscreen="xl-down" onHide={() => setShowFormModal(false)} keyboard={true} centered>
        <Modal.Header closeButton className="border-0 p-4">
          <Modal.Title className="text-primary lh-1">Poptávka<br/>
            <small className="text-muted fs-6">
              {formMode === "create" ? "vložení nového záznamu" : "úprava stávajícího záznamu"}
            </small>            
          </Modal.Title>
        </Modal.Header>
        <Form noValidate validated={validated} onSubmit={handleSave}>
          <Modal.Body className="pt-0">
            <Container fluid className="px-1">
              <Row className="g-1">
                <Col xs={12}>
                  <FloatingLabel label="datum">
                    <Form.Control required type="date" name="datum" value={formData.datum} onChange={handleFormChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={6}> 
                  <FloatingLabel label={<>dodavatel <span className="text-danger">*</span></>}>
                    <Form.Control required type="text" name="dodavatel" list="requests-dodavatel-list" value={formData.dodavatel} onChange={handleFormChange} />
                  </FloatingLabel>
                  <datalist id="requests-dodavatel-list">
                    {suggestions.dodavatel.map((v, i) => (
                      <option key={`dodavatel-${i}-${v}`} value={v} />
                    ))}
                  </datalist>
                </Col>
                <Col xs={6}>
                  <FloatingLabel label={<>zákazník <span className="text-danger">*</span></>}>
                    <Form.Control required type="text" name="zakaznik" list="requests-zakaznik-list" value={formData.zakaznik} onChange={handleFormChange} />
                  </FloatingLabel>
                  <datalist id="requests-zakaznik-list">
                    {suggestions.zakaznik.map((v, i) => (
                      <option key={`zakaznik-${i}-${v}`} value={v} />
                    ))}
                  </datalist>
                </Col>
                <Col xs={12}>
                  <FloatingLabel label="popis">
                    <Form.Control required type="text" name="popis" list="requests-popis-list" value={formData.popis} onChange={handleFormChange} />
                  </FloatingLabel>
                  <datalist id="requests-popis-list">
                    {suggestions.popis.map((v, i) => (
                      <option key={`popis-${i}-${v}`} value={v} />
                    ))}
                  </datalist>
                </Col>
                <Col xs={6}>
                  <FloatingLabel label={<>počet <span className="text-danger">*</span></>}>
                    <Form.Control
                      required
                      type="text"
                      name="pocet"
                      pattern="^\d+(,\d{1,2})?$"
                      title="Zadejte číslo, případně s desetinnou čárkou (max. 2 desetiny)"
                      value={formData.pocet}
                      onChange={handleFormChange}
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={6}>
                  <FloatingLabel label={<>cena <span className="text-danger">*</span></>}>
                    <Form.Control
                      required
                      type="text"
                      name="cena"
                      pattern="^\d+(,\d{1,2})?$"
                      title="Zadejte číslo, případně s desetinnou čárkou (max. 2 desetiny)"
                      value={formData.cena}
                      onChange={handleFormChange}
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={12}>
                  <FloatingLabel label="poznámka">
                    <Form.Control as="textarea" name="poznamka" style={{ minHeight: 90 }} value={formData.poznamka} onChange={handleFormChange} />
                  </FloatingLabel>
                </Col>
                <Col xs={12} className="pt-2">
                  <div className="small text-muted mb-1">stav</div>
                  <ButtonGroup aria-label="Stav poptávky">
                    {REQUEST_STATUSES.map((status) => {
                      const active = Number(formData.stav) === status.code
                      return (
                        <Button
                          key={status.code}
                          type="button"
                          variant={active ? "primary" : "outline-primary"}
                          aria-pressed={active}
                          onClick={() => setFormData((prev) => ({ ...prev, stav: String(status.code) }))}
                        >
                          <i className={`bi ${status.icon} me-2`}></i>{status.label}
                        </Button>
                      )
                    })}
                  </ButtonGroup>
                </Col>
                {isError && (
                  <Col xs={12}>
                    <Alert variant="danger" className="mb-0">Uložení se nezdařilo.</Alert>
                  </Col>
                )}                
              </Row>
            </Container>
          </Modal.Body>
          <Modal.Footer className='border-0 d-flex justify-content-between px-3 pb-3'>
            <div>
            <Button className="me-1" variant="primary" type="submit" disabled={isSaving}>
              {isSaving ? "Ukládám..." : "Uložit"}
            </Button>
            <Button variant="secondary" onClick={() => setShowFormModal(false)}>Zavřít</Button>           
            </div>
            <p className='text-muted small mt-2 mb-0'><span className="text-danger me-1">*</span>označení povinných polí</p>
          </Modal.Footer>
        </Form>
      </Modal>

      {showDeleteModal && selectedItem && (
        <Modal show={showDeleteModal} fullscreen="xl-down" onHide={() => setShowDeleteModal(false)} keyboard={true} size="md" centered>
          <Modal.Header closeButton className="border-0 px-4">
            <Modal.Title className="text-primary mt-3 lh-1">Poptávka<br/>
              <span className="text-muted fs-6">odstranit záznam</span>
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleDelete}>
            <Modal.Body className="px-4">              
              <Row className="g-1">
                <Col xs={12}>
                  <Form.Group controlId="deleteDatum">
                    <FloatingLabel label="datum">
                      <Form.Control
                        type="text"
                        value={toDateDisplay(selectedItem.datum)}
                        readOnly
                        disabled
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group controlId="deletePopis">
                    <FloatingLabel label="popis">
                      <Form.Control
                        type="text"
                        value={selectedItem.popis || ""}
                        readOnly
                        disabled
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group controlId="deleteDodavatel">
                    <FloatingLabel label="dodavatel">
                      <Form.Control
                        type="text"
                        value={selectedItem.dodavatel || ""}
                        readOnly
                        disabled
                      />
                    </FloatingLabel>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  {isDeleteError ? (
                    <Alert variant="danger" className="d-flex align-items-center mt-2 mb-0">
                      <i className="bi bi-exclamation-triangle pe-3 fs-3"></i>
                      <span>Záznam se nepodařilo smazat. Zkuste to prosím znovu.</span>
                    </Alert>
                  ) : (
                    <Alert variant="warning" className="d-flex align-items-center mt-2 mb-0">
                      <i className="bi bi-exclamation-triangle pe-3 fs-3"></i>
                      <span>Chcete skutečně smazat záznam? Tato operace je nevratná.</span>
                    </Alert>
                  )}
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="border-0 d-flex justify-content-start px-4 pb-3">
              {isDeleteError ? (
                <Button variant="secondary ms-1" onClick={() => setShowDeleteModal(false)}>Zavřít</Button>
              ) : (
                <>
                  <Button variant="primary mx-0" type="submit" disabled={isDeleting}>
                    {isDeleting ? "Mazání..." : "Smazat"}
                  </Button>
                  <Button variant="secondary ms-1" onClick={() => setShowDeleteModal(false)}>Zavřít</Button>
                </>
              )}
            </Modal.Footer>
          </Form>
        </Modal>
      )}

      <Notification notifications={notifications} setNotifications={setNotifications} />
    </>
  )
}

export default Requests
